import { Fragment, memo, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, NavLink, useLocation } from "react-router-dom"
import "./style.css"

import {
  AssignmentOutlined as AssignmentOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
} from "@mui/icons-material"

import { Skeleton } from "@mui/material"
import { getMeData } from "app/homepage/slice"
import { useGetReviewsQuery } from "../../pages/reviews/ReviewsSlice"
import { fetchSettings } from "../../pages/settings/settingsSlice"
import { useGetSubscriptionStatusQuery } from "../../pages/subscription/subscriptionApi"

export const Navbar = memo(({ pathList, hidden, setHidden }) => {
  const user = useSelector((state) => state.main.user)
  const dispatch = useDispatch()

  const { step: reduxStep } = useSelector((state) => state.StepSliceReducer)
  const wrapRef = useRef(null)
  const { pathname } = useLocation()
  const { list: settingsList } = useSelector(
    (state) => state.settingsSliceReducer
  )

  // Получаем step из Redux или из user объекта как fallback, или из localStorage
  const step = useMemo(() => {
    if (reduxStep) return reduxStep
    if (user?.step) return user.step
    try {
      const userData = localStorage.getItem("user")
      if (userData) {
        const localUser = JSON.parse(userData)
        return localUser?.step || ""
      }
    } catch {
      // ignore
    }
    return ""
  }, [reduxStep, user?.step])

  const [expandedMenus, setExpandedMenus] = useState({})
  const [loading, setLoading] = useState(true)

  const toggleSubmenu = (e, value) => {
    e.stopPropagation()
    setExpandedMenus(prev => ({
      ...prev,
      [value]: !prev[value]
    }))
  }

  const { data: reviewsData } = useGetReviewsQuery({
    perPage: 10000,
    page: 1,
    limit: 10000,
  })

  // Получаем статус подписки
  const { data: subscriptionData, isLoading: isSubscriptionLoading } = useGetSubscriptionStatusQuery()

  // Проверяем, активна ли подписка PRO (используем useMemo для пересчета при изменении subscriptionData)
  const isProActive = useMemo(() => {
    // Пока данные загружаются — считаем Pro активным (оптимистично),
    // чтобы Лаборатория не мигала при первой загрузке страницы
    if (isSubscriptionLoading) return true
    if (!subscriptionData) return false
    if (subscriptionData.status === "none") return false

    const isPro = subscriptionData.plan?.toLowerCase() === "pro"
    const isApiActive =
      subscriptionData.is_active || subscriptionData.status === "active"
    const daysLeft = subscriptionData.days_left || 0
    const isTimeExpired = daysLeft <= 0

    return isPro && isApiActive && !isTimeExpired
  }, [subscriptionData, isSubscriptionLoading])

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken")
    if (accessToken) {
      dispatch(getMeData()).finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [dispatch])

  // Загружаем настройки для получения direction_id
  useEffect(() => {
    // Используем user из Redux, если он есть, иначе из localStorage
    const currentUser =
      user ||
      (() => {
        try {
          const userData = localStorage.getItem("user")
          return userData ? JSON.parse(userData) : {}
        } catch {
          return {}
        }
      })()

    const salon_id = currentUser?.salon_id || currentUser?.restaurant_id
    if (
      salon_id &&
      (!settingsList?.direction_id || settingsList?.direction_id === 0)
    ) {
      dispatch(fetchSettings(String(salon_id)))
    }
  }, [dispatch, settingsList?.direction_id, user])

  const isAdmin = user?.admin
  const userRoleId = user?.roles?.[0]?.id
  const isAdministratorRole = user?.roles?.some((role) => role.id === 2)

  const ADMIN_VISIBLE_MENU = [
    "Расписание",
    "Обратная связь",
    "Финансы",
    "Справочники",
  ]

  // Получаем retaurant_id с пересчетом при изменении user
  const retaurant_id = useMemo(() => {
    try {
      const stored = localStorage.getItem("direction_id")
      if (stored) {
        const directionId = JSON.parse(stored)
        if (directionId) {
          const restaurantId = localStorage.getItem("retaurant_id")
          if (restaurantId) {
            return JSON.parse(restaurantId)
          }
        }
      }
    } catch {
      // ignore
    }
    return user?.restaurant?.direction_id
  }, [user?.restaurant?.direction_id])

  // Используем direction_id из settingsList с fallback на retaurant_id или user?.restaurant?.direction_id
  const direction_id = useMemo(() => {
    return (
      settingsList?.direction_id ||
      retaurant_id ||
      user?.restaurant?.direction_id ||
      user?.salon?.direction_id
    )
  }, [
    settingsList?.direction_id,
    retaurant_id,
    user?.restaurant?.direction_id,
    user?.salon?.direction_id,
  ])

  // Вычисляем, должна ли отображаться вкладка "Лаборатория"
  const shouldShowLab = useMemo(() => {
    return direction_id === 2 && step && Number(step) >= 4 && isProActive
  }, [direction_id, step, isProActive])

  function entered() {
    setHidden(false)
    wrapRef.current.classList.remove("leaveWidth")
    wrapRef.current.classList.add("enterWidth")
  }

  function leaved() {
    setHidden(true)
    wrapRef.current.classList.remove("enterWidth")
    wrapRef.current.classList.add("leaveWidth")
  }

  if (loading) {
    return (
      <div className='navbar-skeleton sm:p-4 flex flex-col gap-4'>
        <Skeleton variant='rectangular' height={60} animation='wave' />
        <Skeleton
          variant='rectangular'
          height={40}
          width='80%'
          animation='wave'
        />
        <Skeleton
          variant='rectangular'
          height={40}
          width='60%'
          animation='wave'
        />
      </div>
    )
  }

  return (
    <>
      <div
        onClick={() => setExpandedMenus({})}
        className='hidden sm:flex bg-blue px-6'
      >
        <div
          ref={wrapRef}
          className='sticky left-0 top-0 bottom-0 h-screen navContent overflow-y-auto no-scrollbar'
          style={{ width: hidden ? '70px' : '280px', transition: 'width 0.3s' }}
        >
          <div className='mt-10 sm:mt-14 mb-16 flex justify-between items-center'>
            <div className='w-fit h-fit flex text-orange sm:text-2xl text-xl'>
              {!hidden && (
                <img
                  src='/assets/logo/logo.svg'
                  alt='Logo'
                  className='transitionWidth h-5'
                />
              )}
            </div>
            <button
              onClick={hidden ? entered : leaved}
              className='[&>svg]:text-lightGray [&>svg]:hover:text-orange h-fit [&>svg]:w-[20px]'
            >
              {hidden ? (
                <KeyboardDoubleArrowRightOutlined
                  className='w-[20px]'
                  fontSize='small'
                />
              ) : (
                <KeyboardDoubleArrowLeftOutlined className='w-[20px]' />
              )}
            </button>
          </div>
          <nav className='flex flex-col'>
            {pathList.map(
              ({ path, value, Icon, hasSubmenu, submenu }, index) => {
                const valueTrimmed = value.trim()
                const adminNavClass =
                  pathname === path ||
                  (hasSubmenu && pathname.startsWith(path))
                    ? "text-orange [&>div]:text-orange"
                    : "hover:text-orange [&>div]:text-gray group text-white"

                // Администратор (roles id 2) — даже при admin === "0"
                if (isAdministratorRole) {
                  if (!ADMIN_VISIBLE_MENU.includes(valueTrimmed)) return null

                  if (hasSubmenu) {
                    return (
                      <Fragment key={path}>
                        <div
                          onClick={(e) => toggleSubmenu(e, valueTrimmed)}
                          className={`${adminNavClass} flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group cursor-pointer`}
                        >
                          <div
                            style={{
                              width: "20px",
                              marginRight: hidden ? "0px" : "15px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {
                              <Icon
                                sx={{
                                  width: "20px",
                                  color: pathname.startsWith(path)
                                    ? "secondary.main"
                                    : "inherit",
                                }}
                              />
                            }
                          </div>
                          {!hidden && (
                            <>
                              <p className='transitional transitionAppearance w-fit pr-[10px]'>
                                {value}
                              </p>
                              <div className='text-white hover:text-orange'>
                                {expandedMenus[valueTrimmed] ? (
                                  <KeyboardArrowDownIcon />
                                ) : (
                                  <KeyboardArrowRightIcon />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        {!hidden && expandedMenus[valueTrimmed] && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className='ml-10 flex flex-col mb-4'
                          >
                            {submenu.map((subItem) => (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                className={`${
                                  pathname === subItem.path
                                    ? "text-orange [&>div]:text-orange"
                                    : "text-white"
                                } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group`}
                              >
                                {!hidden && (
                                  <span className='transitional transitionAppearance w-fit pr-[10px]'>
                                    {subItem.value}
                                  </span>
                                )}
                              </Link>
                            ))}
                          </div>
                        )}
                      </Fragment>
                    )
                  }

                  return (
                    <div key={path}>
                      <NavLink
                        to={path}
                        className={`${adminNavClass} flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                      >
                        <div
                          style={{
                            width: "20px",
                            marginRight: hidden ? "0px" : "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {
                            <Icon
                              sx={{
                                width: "20px",
                                color: pathname === path
                                  ? "secondary.main"
                                  : "inherit",
                              }}
                            />
                          }
                        </div>
                        {!hidden && (
                          <span className='transitional transitionAppearance w-fit pr-[10px]'>
                            {value}
                          </span>
                        )}
                      </NavLink>
                    </div>
                  )
                }

                // Мастер: admin === "0" без роли администратора
                if (isAdmin === "0") {
                  if (
                    !["Обратная связь", "Расписание"].includes(valueTrimmed)
                  ) {
                    return null
                  }
                  return (
                    <div key={path}>
                      <NavLink
                        to={path}
                        className={`${
                          pathname === path
                            ? "text-orange [&>div]:text-orange"
                            : "hover:text-orange [&>div]:text-gray group text-white"
                        } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                      >
                        <div
                          style={{
                            width: "20px",
                            marginRight: hidden ? "0px" : "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {
                            <Icon
                              sx={{
                                width: "20px",
                                color:
                                  pathname === path
                                    ? "secondary.main"
                                    : "inherit",
                              }}
                            />
                          }
                        </div>
                        {!hidden && (
                          <span className='transitional transitionAppearance w-fit pr-[10px]'>
                            {value}
                          </span>
                        )}
                      </NavLink>
                    </div>
                  )
                }
                // Если admin === 1, показываем все разделы (остальные условия ниже не трогаем)
                // Остальная логика (шаги, лаборатория и т.д.) остается без изменений
                if (valueTrimmed === "Обратная связь") {
                  return (
                    <div key={path}>
                      <NavLink
                        to={path}
                        className={`${
                          pathname === path
                            ? "text-orange [&>div]:text-orange"
                            : "hover:text-orange [&>div]:text-gray group text-white"
                        } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                      >
                        <div 
                          style={{ 
                            width: "20px", 
                            marginRight: hidden ? "0px" : "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          {
                            <Icon
                              sx={{
                                width: "20px",
                                color:
                                  pathname === path
                                    ? "secondary.main"
                                    : "inherit",
                              }}
                            />
                          }
                        </div>
                        {!hidden && (
                          <span className='transitional transitionAppearance w-fit pr-[10px]'>
                            {value}
                          </span>
                        )}
                      </NavLink>
                    </div>
                  )
                }

                const isAlwaysVisible =
                  retaurant_id !== 6 &&
                  userRoleId === 6 &&
                  // userRoleId === 2 &&
                  ["Расписание", "Обратная связь"].includes(valueTrimmed)

                if (isAlwaysVisible) {
                  return (
                    <div key={path}>
                      <NavLink
                        to={path}
                        className={`${
                          pathname === path
                            ? "text-orange [&>div]:text-orange"
                            : "hover:text-orange [&>div]:text-gray group text-white"
                        } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                      >
                        <div 
                          style={{ 
                            width: "20px", 
                            marginRight: hidden ? "0px" : "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          {
                            <Icon
                              sx={{
                                width: "20px",
                                color:
                                  pathname === path
                                    ? "secondary.main"
                                    : "inherit",
                              }}
                            />
                          }
                        </div>
                        {!hidden && (
                          <span className='transitional transitionAppearance w-fit pr-[10px]'>
                            {value}
                          </span>
                        )}
                      </NavLink>
                    </div>
                  )
                }

                if (retaurant_id === 6) {
                  const isVisibleForCategory1 = [
                    "Записи",
                    "Клиенты",
                    "Обратная связь",
                    "Настройки",
                  ].includes(valueTrimmed)

                  return (
                    isVisibleForCategory1 && (
                      <div key={path}>
                        <NavLink
                          to={path}
                          className={`${
                            pathname === path
                              ? "text-orange [&>div]:text-orange"
                              : "hover:text-orange [&>div]:text-gray group text-white"
                          } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                        >
                          <div 
                            style={{ 
                              width: "20px", 
                              marginRight: hidden ? "0px" : "15px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            {
                              <Icon
                                sx={{
                                  width: "20px",
                                  color: `${pathname === path && "secondary.main"}`,
                                }}
                              />
                            }
                          </div>
                          {!hidden && (
                            <span className='transitional transitionAppearance w-fit pr-[10px]'>
                              {value}
                            </span>
                          )}
                        </NavLink>
                      </div>
                    )
                  )
                }

                // Логика для остальных категорий с шагами (step)
                const hideStepItems =
                  userRoleId === 6 ||
                  (userRoleId === 2 &&
                    [
                      "Категории услуг",
                      "Услуги",
                      "Настройки",
                      "Сотрудники",
                      "Клиенты",
                      "Записи",
                      "Финансы",
                      "Лаборатория",
                    ].includes(valueTrimmed))

                const stepClass =
                  pathname === path || (hasSubmenu && pathname.startsWith(path))
                    ? "text-orange [&>div]:text-orange"
                    : step && Number(step) >= 5
                      ? "hover:text-orange [&>div]:text-gray group text-white"
                      : step === "0" && ["Настройки"].includes(valueTrimmed)
                        ? "hover:text-orange [&>div]:text-gray group text-white"
                        : step === "1" &&
                            ["Категории услуг", "Настройки"].includes(
                              valueTrimmed
                            )
                          ? "hover:text-orange [&>div]:text-gray group text-white"
                          : step === "2" &&
                              [
                                "Категории услуг",
                                "Услуги",
                                "Настройки",
                              ].includes(valueTrimmed)
                            ? "hover:text-orange [&>div]:text-gray group text-white"
                            : step === "3" &&
                                [
                                  "Категории услуг",
                                  "Услуги",
                                  "Настройки",
                                  "Сотрудники",
                                ].includes(valueTrimmed)
                              ? "hover:text-orange [&>div]:text-gray group text-white"
                              : step === "4" &&
                                  [
                                    "Категории услуг",
                                    "Услуги",
                                    "Настройки",
                                    "Сотрудники",
                                    "Клиенты",
                                    "Записи",
                                    "Финансы",
                                    "Лаборатория",
                                    "Отзывы",
                                    "Расписание",
                                  ].includes(valueTrimmed)
                                ? "hover:text-orange [&>div]:text-gray group text-white"
                                : step === ""
                                  ? "text-white" // Show all items normally when step is not yet initialized
                                  : "text-[#9a9c9a] pointer-events-none"

                return (
                  <div key={path}>
                    {!hideStepItems && (
                      <>
                        {hasSubmenu ? (
                          <>
                            <div
                              onClick={
                                stepClass.includes("pointer-events-none")
                                  ? undefined
                                  : (e) => toggleSubmenu(e, valueTrimmed)
                              }
                              className={`${stepClass} flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group cursor-pointer`}
                              style={
                                stepClass.includes("pointer-events-none")
                                  ? { cursor: "not-allowed" }
                                  : {}
                              }
                            >
                              <div 
                                style={{ 
                                  width: "20px", 
                                  marginRight: hidden ? "0px" : "15px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                              >
                                {
                                  <Icon
                                    sx={{
                                      width: "20px",
                                      color: pathname.startsWith(path) ? "secondary.main" : "inherit",
                                    }}
                                  />
                                }
                              </div>
                              {!hidden && (
                                <>
                                  <p
                                    className={`transitional transitionAppearance w-fit pr-[10px]`}
                                  >
                                    {value}
                                  </p>
                                  <div className='text-white hover:text-orange'>
                                    {expandedMenus[valueTrimmed] ? (
                                      <KeyboardArrowDownIcon />
                                    ) : (
                                      <KeyboardArrowRightIcon />
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            {!hidden && expandedMenus[valueTrimmed] &&
                              !stepClass.includes("pointer-events-none") && (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  className='ml-10 flex flex-col mb-4'
                                >
                                  {submenu.map((subItem) => (
                                    <Link
                                      key={subItem.path}
                                      to={subItem.path}
                                      className={`${
                                        pathname === subItem.path
                                          ? "text-orange [&>div]:text-orange"
                                          : "text-white"
                                      }
                                  flex items-center w-full whitespace-nowrap mb-3 sm:mb-7
                                  sm:text-lg font-normal hover:text-orange [&>div]:text-gray group
                                `}
                                    >
                                      {!hidden && (
                                        <span className='transitional transitionAppearance w-fit pr-[10px]'>
                                          {subItem.value}
                                        </span>
                                      )}
                                    </Link>
                                  ))}
                                </div>
                              )}
                          </>
                        ) : (
                          <NavLink
                            to={path}
                            className={`
            ${stepClass}
            flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal        
          `}
                          >
                            <div 
                              style={{ 
                                width: "20px", 
                                marginRight: hidden ? "0px" : "15px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                              }}
                            >
                              {
                                <Icon
                                  sx={{
                                    width: "20px",
                                    color: `${pathname === path && "secondary.main"}`,
                                  }}
                                />
                              }
                            </div>
                            {!hidden && (
                              <span className='transitional transitionAppearance w-fit pr-[10px]'>
                                {value}
                                {value === "Отзывы" &&
                                  reviewsData?.new_reviews > 0 && (
                                    <span className='ml-2 bg-orange  text-white text-xs px-3 py-[2px] rounded-full'>
                                      {reviewsData?.new_reviews}
                                    </span>
                                  )}
                              </span>
                            )}
                          </NavLink>
                        )}
                      </>
                    )}
                    {valueTrimmed === "Услуги" && shouldShowLab && (
                      <>
                        <div
                          onClick={(e) => toggleSubmenu(e, "Лаборатория")}
                          className={`${
                            (pathname === "/laboratory" || pathname.startsWith("/templates") || pathname.startsWith("/analyzes"))
                              ? "text-orange [&>div]:text-orange"
                              : "text-white hover:text-orange [&>div]:text-gray group"
                          } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal cursor-pointer`}
                        >
                          <div 
                            style={{ 
                              width: "20px", 
                              marginRight: hidden ? "0px" : "15px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            {
                              <AssignmentOutlinedIcon
                                sx={{
                                  width: "20px",
                                  color: (pathname === "/laboratory" || pathname.startsWith("/templates") || pathname.startsWith("/analyzes")) ? "secondary.main" : "inherit",
                                }}
                              />
                            }
                          </div>
                          {!hidden && (
                            <>
                              <p
                                className={`transitional transitionAppearance w-fit pr-[10px]`}
                              >
                                Лаборатория
                              </p>
                              <div className='text-white hover:text-orange'>
                                {expandedMenus["Лаборатория"] ? (
                                  <KeyboardArrowDownIcon />
                                ) : (
                                  <KeyboardArrowRightIcon />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        {!hidden && expandedMenus["Лаборатория"] && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className='ml-10 flex flex-col mb-4'
                          >
                            <Link
                              to='/templates'
                              className={`${
                                pathname === "/templates"
                                  ? "text-orange [&>div]:text-orange"
                                  : "text-white"
                              }
                            flex items-center w-full whitespace-nowrap mb-3 sm:mb-7
                            sm:text-lg font-normal hover:text-orange [&>div]:text-gray group
                          `}
                            >
                              Шаблоны
                            </Link>
                            <Link
                              to='/analyzes'
                              className={`${
                                pathname.includes("/analyzes")
                                  ? "text-orange [&>div]:text-orange"
                                  : "text-white"
                              }
                            flex items-center w-full whitespace-nowrap mb-3 sm:mb-7
                            sm:text-lg font-normal hover:text-orange [&>div]:text-gray group
                          `}
                            >
                              Анализы
                            </Link>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              }
            )}
          </nav>
        </div>
      </div>
    </>
  )
})